import { FunctionComponent, useMemo, useCallback, useEffect, DragEvent } from 'react';
import {
    ReactFlow,
    Background,
    Controls,
    BackgroundVariant,
    NodeTypes,
    EdgeTypes,
    ReactFlowProvider,
    NodeMouseHandler,
    useNodesState,
    useEdgesState
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import TriggerNode from './nodes/TriggerNode';
import DelayNode from './nodes/DelayNode';
import SplitNode from './nodes/SplitNode';
import ActionNode from './nodes/ActionNode';
import AddNode from './nodes/AddNode';
import BranchEdge from './edges/BranchEdge';
import { canvasLayout } from './canvasLayout';
import { FlowStep, InsertableType, findStepById } from './useAutomationFlow';
import { FlowProvider, useFlowContext } from '../Wizard/FlowContext';
import styles from './AutomationCanvas.module.scss';

const nodeTypes: NodeTypes = {
    trigger: TriggerNode,
    delay: DelayNode,
    split: SplitNode,
    action: ActionNode,
    add: AddNode
};

// Custom edge type renders the Yes/No pill label on curved branch connections
const edgeTypes: EdgeTypes = {
    branch: BranchEdge
};

export interface AutomationCanvasProps {
    steps: FlowStep[];
    onInsert: (afterStepId: string, type: InsertableType) => void;
    onInsertBranch: (
        splitId: string,
        branch: 'yes' | 'no',
        afterBranchStepId: string | null,
        type: InsertableType
    ) => void;
    onRemove: (stepId: string) => void;
    onFlipSplit?: (stepId: string) => void;
    onTriggerClick?: () => void;
    onDelayClick?: (stepId: string) => void;
    onSplitClick?: (stepId: string) => void;
    onSmsClick?: (stepId: string) => void;
    onEmailClick?: (stepId: string) => void;
    /** Static, non-interactive rendering — used for the AI-generated automation preview. */
    readOnly?: boolean;
}

const CanvasInner: FunctionComponent<AutomationCanvasProps> = ({
    steps,
    onInsert,
    onInsertBranch,
    onRemove,
    onFlipSplit,
    onTriggerClick,
    onDelayClick,
    onSplitClick,
    onSmsClick,
    onEmailClick,
    readOnly
}) => {
    const { draggingType } = useFlowContext();
    const handleStepClick = useCallback(
        (stepId: string) => {
            const step = findStepById(stepId, steps);
            if (!step) return;
            if (step.type === 'delay') onDelayClick?.(stepId);
            else if (step.type === 'split') onSplitClick?.(stepId);
            else if (step.type === 'sms') onSmsClick?.(stepId);
            else if (step.type === 'email') onEmailClick?.(stepId);
        },
        [steps, onDelayClick, onSplitClick, onSmsClick, onEmailClick]
    );

    const { nodes: rawNodes, edges } = useMemo(
        () => canvasLayout(steps, onInsert, onInsertBranch, onRemove, handleStepClick, onFlipSplit, readOnly),
        [steps, onInsert, onInsertBranch, onRemove, handleStepClick, onFlipSplit, readOnly]
    );

    const computedNodes = useMemo(
        () => rawNodes.map(n => (n.type === 'add' ? { ...n, data: { ...n.data, isDragging: !!draggingType } } : n)),
        [rawNodes, draggingType]
    );

    // Handing ReactFlow a brand-new array on every render left its internal store
    // half-applied: add nodes and their edges went missing without any error.
    // Its own state hooks plus a sync effect is the pattern it expects.
    const [nodes, setNodes, onNodesChange] = useNodesState(computedNodes);
    const [flowEdges, setEdges, onEdgesChange] = useEdgesState(edges);

    useEffect(() => {
        setNodes(computedNodes);
    }, [computedNodes, setNodes]);

    useEffect(() => {
        setEdges(edges);
    }, [edges, setEdges]);

    const handleWrapperDragOver = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
    };

    const handleNodeClick = useCallback<NodeMouseHandler>(
        (_, node) => {
            if (node.id === 'trigger-1') onTriggerClick?.();
            else if (node.type === 'delay') onDelayClick?.(node.id);
            else if (node.type === 'split') onSplitClick?.(node.id);
        },
        [onTriggerClick, onDelayClick, onSplitClick]
    );

    return (
        <div
            className={styles.canvasWrapper}
            onDragOver={readOnly ? undefined : handleWrapperDragOver}
        >
            <ReactFlow
                nodes={nodes}
                edges={flowEdges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                nodeTypes={nodeTypes}
                edgeTypes={edgeTypes}
                onNodeClick={readOnly ? undefined : handleNodeClick}
                fitView
                fitViewOptions={{ padding: 0.4, maxZoom: 1 }}
                nodesDraggable={false}
                nodesConnectable={false}
                // Backspace is ReactFlow's default delete key, so editing any
                // sidebar field wiped whichever node was selected behind it.
                deleteKeyCode={null}
                multiSelectionKeyCode={null}
                panOnScroll={!readOnly}
                zoomOnScroll={!readOnly}
                minZoom={0.3}
                maxZoom={1.5}
                proOptions={{ hideAttribution: true }}
            >
                <Background
                    variant={BackgroundVariant.Dots}
                    gap={22}
                    size={1.4}
                    color="#e0e0e0"
                />
                {!readOnly && (
                    <Controls
                        showInteractive={false}
                        position="top-left"
                    />
                )}
            </ReactFlow>
        </div>
    );
};

const AutomationCanvas: FunctionComponent<AutomationCanvasProps> = props => (
    <ReactFlowProvider>
        <CanvasInner {...props} />
    </ReactFlowProvider>
);

export default AutomationCanvas;
